import {
  liveTurnsAwaitingHistory,
  mergePinnedSessionsIntoRecency,
} from 'features/embeddedSidebar/sessions/agenticChatSessionList'
import {
  hydrateDocumentChatConversation,
  shouldFetchDocumentChatSnapshot,
} from 'features/embeddedSidebar/sessions/documentChatConversationHydration'
import {
  type AgenticChatSessionSyncError,
  buildDocumentChatSessionArchivePatch,
  buildDocumentChatSessionDeletePatch,
  buildDocumentChatSessionLoadOptions,
  buildDocumentChatSessionPinPatch,
  buildDocumentChatSessionRenamePatch,
  sessionsFromRolodexEntries,
} from 'features/embeddedSidebar/sessions/documentChatSessionRolodex'
import {
  DOCUMENT_CHAT_CONVERSATION_FILE_PATH,
  DOCUMENT_CHAT_SESSION_PAGE_SIZE,
  documentChatRestoreMode,
  parseDocumentChatConversationSnapshot,
} from 'features/embeddedSidebar/sessions/documentChatSessionSchemas'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { DocChatConversation } from 'features/knowledgeDocumentViewer/types'
import { useGeckoPermissions } from 'features/permissions'
import { useFileSignedUrlFetcher, useRolodexEntries } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type {
  AgenticChatSessionSyncStatus,
  RecordedAgenticChatSession,
} from './useAgenticChatSessionHistory'
import { useHistoryCatchUpPolling } from './useHistoryCatchUpPolling'
import {
  type RestoredDocChatTurnSettlement,
  useRestoredDocChatTurnSettlement,
} from './useRestoredDocChatTurnSettlement'

type SessionSyncState = {
  error?: AgenticChatSessionSyncError
  status: AgenticChatSessionSyncStatus
}

type PinOverride = {
  pinned: boolean
  pinnedAt?: number
}

function applySessionDisplayState(
  sessions: RecordedAgenticChatSession[],
  syncByKey: Record<string, SessionSyncState>,
  titleOverrides: Record<string, string>,
  pinOverrides: Record<string, PinOverride>
): RecordedAgenticChatSession[] {
  return sessions.map((session) => {
    const sync = syncByKey[session.key]
    const title = titleOverrides[session.key]
    const pin = pinOverrides[session.key]
    if (!sync && title === undefined && pin === undefined) return session
    return {
      ...session,
      ...(sync === undefined
        ? {}
        : {
            persistError: sync.error,
            syncStatus: sync.status,
          }),
      ...(title === undefined ? {} : { customTitle: title, title }),
      ...(pin === undefined
        ? {}
        : { pinned: pin.pinned, pinnedAt: pin.pinnedAt }),
    }
  })
}

export function useDocumentChatSessions({
  liveSessions,
  showHistory = false,
  onRestoredTurnSettled,
  siteId,
}: {
  liveSessions: RecordedAgenticChatSession[]
  showHistory?: boolean
  onRestoredTurnSettled?: (update: RestoredDocChatTurnSettlement) => void
  siteId: string | undefined
}): {
  activeLoadFailed: boolean
  archiveSession: (sessionKey: string) => Promise<boolean>
  archiveSessions: (sessions: RecordedAgenticChatSession[]) => Promise<boolean>
  archivedHasMore: boolean
  archivedSessions: RecordedAgenticChatSession[]
  deleteSession: (sessionKey: string) => Promise<boolean>
  deleteSessions: (sessionKeys: string[]) => Promise<boolean>
  deletedSessionKeys: ReadonlySet<string>
  hasArchivedSessions: boolean
  hasExpandedArchived: boolean
  hydrateSession: (
    session: RecordedAgenticChatSession,
    options?: { force?: boolean }
  ) => Promise<DocChatConversation | null>
  isLoadingActive: boolean
  isLoadingArchived: boolean
  loadMoreArchived: () => void
  persistedSessions: RecordedAgenticChatSession[]
  pinPersistedSession: (sessionKey: string, pinned: boolean) => Promise<boolean>
  renamePersistedSession: (
    sessionKey: string,
    title: string
  ) => Promise<boolean>
  retryActiveLoad: () => Promise<void>
  retrySession: (session: RecordedAgenticChatSession) => Promise<void>
  toggleArchived: () => void
  unarchiveSession: (sessionKey: string) => Promise<boolean>
  unarchiveSessions: (
    sessions: RecordedAgenticChatSession[]
  ) => Promise<boolean>
} {
  const { user } = useGeckoPermissions()
  const userId = user?.id
  const { rolodexPatch } = useRolodexEdit()
  const { fetchSignedUrl } = useFileSignedUrlFetcher()
  const [hasExpandedArchived, setHasExpandedArchived] = useState(false)
  const [archivedLimit, setArchivedLimit] = useState(
    DOCUMENT_CHAT_SESSION_PAGE_SIZE
  )
  const [syncByKey, setSyncByKey] = useState<Record<string, SessionSyncState>>(
    {}
  )
  const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>(
    {}
  )
  const [pinOverrides, setPinOverrides] = useState<Record<string, PinOverride>>(
    {}
  )
  const [pendingArchiveChanges, setPendingArchiveChanges] = useState<
    Record<string, RecordedAgenticChatSession>
  >({})
  const inFlightUnarchives = useRef(new Set<string>())
  const titleByKey = useRef(new Map<string, string>())
  const [deletedSessionKeys, setDeletedSessionKeys] = useState<
    ReadonlySet<string>
  >(() => new Set())

  const activeQuery = useMemo(
    () =>
      buildDocumentChatSessionLoadOptions({
        archived: false,
        siteId,
        userId,
      }),
    [siteId, userId]
  )
  const archivedQuery = useMemo(
    () =>
      hasExpandedArchived
        ? buildDocumentChatSessionLoadOptions({
            archived: true,
            limit: archivedLimit,
            siteId,
            userId,
          })
        : undefined,
    [archivedLimit, hasExpandedArchived, siteId, userId]
  )
  const archivedExistenceQuery = useMemo(
    () =>
      showHistory
        ? buildDocumentChatSessionLoadOptions({
            archived: true,
            limit: 1,
            siteId,
            userId,
          })
        : undefined,
    [showHistory, siteId, userId]
  )
  const pinnedQuery = useMemo(
    () =>
      buildDocumentChatSessionLoadOptions({
        archived: false,
        pinned: true,
        siteId,
        userId,
      }),
    [siteId, userId]
  )

  const {
    entries: activeEntries,
    error: activeError,
    hasEverLoaded: hasLoadedActive,
    forceRefresh: refreshActive,
    isLoading: isLoadingActive,
  } = useRolodexEntries(activeQuery, {
    allowCache: false,
    swrOptions: { revalidateOnFocus: true },
  })
  const {
    entries: archivedEntries,
    forceRefresh: refreshArchived,
    isLoading: isLoadingArchived,
  } = useRolodexEntries(archivedQuery, {
    allowCache: false,
    swrOptions: { revalidateOnFocus: true },
  })
  const {
    entries: archivedExistenceEntries,
    forceRefresh: refreshArchivedExistence,
  } = useRolodexEntries(archivedExistenceQuery, {
    allowCache: false,
    swrOptions: { revalidateOnFocus: true },
  })
  const {
    entries: pinnedEntries,
    forceRefresh: refreshPinned,
    hasEverLoaded: hasLoadedPinned,
    error: pinnedError,
  } = useRolodexEntries(pinnedQuery, {
    allowCache: false,
    swrOptions: { revalidateOnFocus: true },
  })
  const isHistoryReady =
    Boolean(activeQuery) &&
    hasLoadedActive &&
    hasLoadedPinned &&
    !activeError &&
    !pinnedError

  const persistedActive = useMemo(() => {
    if (!siteId) return []
    return mergePinnedSessionsIntoRecency(
      sessionsFromRolodexEntries(activeEntries, siteId),
      sessionsFromRolodexEntries(pinnedEntries, siteId)
    )
  }, [activeEntries, pinnedEntries, siteId])
  const persistedArchived = useMemo(() => {
    if (!siteId) return []
    return sessionsFromRolodexEntries(archivedEntries, siteId)
  }, [archivedEntries, siteId])

  useEffect(() => {
    setPendingArchiveChanges((current) => {
      const acknowledged = Object.keys(current).filter(
        (key) =>
          !liveSessions.some((session) => session.key === key) &&
          (current[key].archived
            ? persistedArchived.some((session) => session.key === key)
            : persistedActive.some((session) => session.key === key) &&
              !persistedArchived.some((session) => session.key === key) &&
              !sessionsFromRolodexEntries(
                archivedExistenceEntries,
                siteId ?? ''
              ).some((session) => session.key === key))
      )
      if (acknowledged.length === 0) return current
      const next = { ...current }
      acknowledged.forEach((key) => delete next[key])
      return next
    })
  }, [
    archivedExistenceEntries,
    liveSessions,
    persistedActive,
    persistedArchived,
    siteId,
  ])

  const persistedSessions = useMemo(
    () =>
      applySessionDisplayState(
        [
          ...liveSessions.filter(
            (session) =>
              syncByKey[session.key] &&
              !persistedActive.some((item) => item.key === session.key) &&
              !persistedArchived.some((item) => item.key === session.key) &&
              !pendingArchiveChanges[session.key]
          ),
          ...persistedActive,
          ...persistedArchived,
          ...Object.values(pendingArchiveChanges).filter(
            (session) => session.siteId === siteId
          ),
        ],
        syncByKey,
        titleOverrides,
        pinOverrides
      ),
    [
      liveSessions,
      persistedActive,
      persistedArchived,
      pendingArchiveChanges,
      siteId,
      pinOverrides,
      syncByKey,
      titleOverrides,
    ]
  )

  const setSessionSync = useCallback(
    (sessionKey: string, sync: SessionSyncState | null) => {
      setSyncByKey((current) => {
        if (sync === null) {
          if (!(sessionKey in current)) return current
          const next = { ...current }
          delete next[sessionKey]
          return next
        }
        return { ...current, [sessionKey]: sync }
      })
    },
    []
  )

  const refreshLists = useCallback(async () => {
    await refreshActive()
    await refreshPinned()
    if (showHistory) await refreshArchivedExistence()
    if (hasExpandedArchived) await refreshArchived()
  }, [
    hasExpandedArchived,
    refreshActive,
    refreshArchived,
    refreshArchivedExistence,
    refreshPinned,
    showHistory,
  ])

  const awaitingHistory = useMemo(
    () =>
      liveTurnsAwaitingHistory({
        liveSessions: liveSessions.filter(
          (session) => !deletedSessionKeys.has(session.key)
        ),
        persistedSessions,
      }).join('|'),
    [deletedSessionKeys, liveSessions, persistedSessions]
  )

  useHistoryCatchUpPolling({
    isReady: isHistoryReady,
    awaitingKeys: awaitingHistory,
    refresh: refreshLists,
  })

  const hydrateSession = useCallback(
    async (
      session: RecordedAgenticChatSession,
      options?: { force?: boolean }
    ): Promise<DocChatConversation | null> => {
      const force = options?.force === true
      if (
        !force &&
        session.isHydrated &&
        session.conversation.turns.length > 0
      ) {
        return session.conversation
      }
      if (!shouldFetchDocumentChatSnapshot(session)) {
        const conversation = hydrateDocumentChatConversation({
          session,
          force,
        })
        if (!conversation) {
          setSessionSync(session.key, { status: 'error', error: 'hydrate' })
          return null
        }
        setSessionSync(session.key, null)
        return conversation
      }
      if (!session.conversation.id) return null
      setSessionSync(session.key, { status: 'hydrating' })
      try {
        const signed = await fetchSignedUrl({
          queryOrQueries: {
            entry_id: session.conversation.id,
            path: DOCUMENT_CHAT_CONVERSATION_FILE_PATH,
          },
        })
        const signedUrl = signed?.url
        if (!signedUrl) throw new Error('Missing snapshot URL')
        const response = await fetch(signedUrl)
        if (!response.ok) throw new Error('Snapshot download failed')
        const snapshot = parseDocumentChatConversationSnapshot(
          await response.json()
        )
        if (!snapshot) throw new Error('Invalid snapshot')
        const conversation = hydrateDocumentChatConversation({
          session,
          snapshot,
          force,
        })
        if (!conversation) throw new Error('Invalid snapshot')
        setSessionSync(session.key, null)
        return conversation
      } catch {
        const restoreMode = documentChatRestoreMode(session)
        const overlayFallback =
          restoreMode === 'running' ||
          restoreMode === 'failed' ||
          restoreMode === 'cancelled'
            ? hydrateDocumentChatConversation({ session, force })
            : null
        if (overlayFallback) {
          setSessionSync(session.key, null)
          return overlayFallback
        }
        setSessionSync(session.key, { status: 'error', error: 'hydrate' })
        return null
      }
    },
    [fetchSignedUrl, setSessionSync]
  )

  useRestoredDocChatTurnSettlement({
    hydrateSession,
    liveSessions,
    onRestoredTurnSettled,
    persistedSessions,
    refreshActive,
    refreshPinned,
  })

  const renamePersistedSession = useCallback(
    async (sessionKey: string, title: string): Promise<boolean> => {
      if (!sessionKey.startsWith('power-doc-search-conversation')) {
        return true
      }
      titleByKey.current.set(sessionKey, title)
      setTitleOverrides((current) =>
        current[sessionKey] === title
          ? current
          : { ...current, [sessionKey]: title }
      )
      try {
        await rolodexPatch([
          buildDocumentChatSessionRenamePatch(sessionKey, title),
        ])
        setSessionSync(sessionKey, null)
        await refreshLists()
        return true
      } catch {
        setTitleOverrides((current) => {
          if (!(sessionKey in current)) return current
          const next = { ...current }
          delete next[sessionKey]
          return next
        })
        setSessionSync(sessionKey, { status: 'error', error: 'rename' })
        return false
      }
    },
    [refreshLists, rolodexPatch, setSessionSync]
  )

  const pinPersistedSession = useCallback(
    async (sessionKey: string, pinned: boolean): Promise<boolean> => {
      const nextPin: PinOverride = pinned
        ? { pinned: true, pinnedAt: Date.now() }
        : { pinned: false }
      setPinOverrides((current) => ({ ...current, [sessionKey]: nextPin }))
      if (!sessionKey.startsWith('power-doc-search-conversation')) {
        return true
      }
      try {
        await rolodexPatch([
          buildDocumentChatSessionPinPatch({
            conversationId: sessionKey,
            pinned,
            pinnedAt: nextPin.pinnedAt,
          }),
        ])
        setSessionSync(sessionKey, null)
        await refreshLists()
        return true
      } catch {
        setPinOverrides((current) => {
          if (!(sessionKey in current)) return current
          const next = { ...current }
          delete next[sessionKey]
          return next
        })
        setSessionSync(sessionKey, {
          status: 'error',
          error: pinned ? 'pin' : 'unpin',
        })
        return false
      }
    },
    [refreshLists, rolodexPatch, setSessionSync]
  )

  const setSessionsArchived = useCallback(
    async (
      sessions: RecordedAgenticChatSession[],
      archived: boolean
    ): Promise<boolean> => {
      if (sessions.length === 0) return true
      const archivedAt = Date.now()
      const syncStatus = archived ? 'archiving' : 'unarchiving'
      sessions.forEach((session) =>
        setSessionSync(session.key, { status: syncStatus })
      )
      try {
        await rolodexPatch(
          sessions.map((session) =>
            buildDocumentChatSessionArchivePatch({
              archived,
              archivedAt,
              conversationId: session.key,
            })
          )
        )
      } catch {
        sessions.forEach((session) =>
          setSessionSync(session.key, {
            status: 'error',
            error: archived ? 'archive' : 'unarchive',
          })
        )
        return false
      }
      setPendingArchiveChanges((current) => ({
        ...current,
        ...Object.fromEntries(
          sessions.map((session) => [
            session.key,
            {
              ...session,
              archived,
              archivedOverride: archived,
              persistError: undefined,
              syncStatus: 'idle',
            },
          ])
        ),
      }))
      sessions.forEach((session) => setSessionSync(session.key, null))
      // The archive change has committed even if a subsequent list refresh fails.
      await refreshLists().catch(() => undefined)
      return true
    },
    [refreshLists, rolodexPatch, setSessionSync]
  )

  const archiveSessions = useCallback(
    (sessions: RecordedAgenticChatSession[]) =>
      setSessionsArchived(sessions, true),
    [setSessionsArchived]
  )
  const unarchiveSessions = useCallback(
    (sessions: RecordedAgenticChatSession[]) =>
      setSessionsArchived(sessions, false),
    [setSessionsArchived]
  )

  const unarchiveSession = useCallback(
    async (sessionKey: string): Promise<boolean> => {
      if (inFlightUnarchives.current.has(sessionKey)) return false
      const session =
        persistedSessions.find((item) => item.key === sessionKey) ??
        liveSessions.find((item) => item.key === sessionKey)
      if (!session) return false
      inFlightUnarchives.current.add(sessionKey)
      try {
        return await unarchiveSessions([session])
      } finally {
        inFlightUnarchives.current.delete(sessionKey)
      }
    },
    [liveSessions, persistedSessions, unarchiveSessions]
  )

  const archiveSession = useCallback(
    async (sessionKey: string): Promise<boolean> => {
      if (!sessionKey.startsWith('power-doc-search-conversation')) return true
      const session =
        persistedSessions.find((item) => item.key === sessionKey) ??
        liveSessions.find((item) => item.key === sessionKey)
      return session ? archiveSessions([session]) : false
    },
    [archiveSessions, liveSessions, persistedSessions]
  )

  const deleteSessions = useCallback(
    async (sessionKeys: string[]): Promise<boolean> => {
      const keys = [...new Set(sessionKeys)].filter(
        (key) =>
          key.startsWith('power-doc-search-conversation') &&
          !deletedSessionKeys.has(key)
      )
      if (keys.length === 0) return true
      const deletedAt = Date.now()
      keys.forEach((key) => setSessionSync(key, { status: 'deleting' }))
      try {
        await rolodexPatch(
          keys.map((key) =>
            buildDocumentChatSessionDeletePatch({
              conversationId: key,
              deletedAt,
            })
          )
        )
      } catch {
        keys.forEach((key) =>
          setSessionSync(key, { status: 'error', error: 'delete' })
        )
        return false
      }
      setDeletedSessionKeys((current) => new Set([...current, ...keys]))
      keys.forEach((key) => setSessionSync(key, null))
      // A refresh failure cannot roll back a committed deletion.
      await refreshLists().catch(() => undefined)
      return true
    },
    [deletedSessionKeys, refreshLists, rolodexPatch, setSessionSync]
  )

  const deleteSession = useCallback(
    (sessionKey: string): Promise<boolean> => deleteSessions([sessionKey]),
    [deleteSessions]
  )

  const retrySession = useCallback(
    async (session: RecordedAgenticChatSession) => {
      const error = session.persistError
      if (error === 'hydrate') {
        await hydrateSession(session)
        return
      }
      if (error === 'rename') {
        const title = titleByKey.current.get(session.key) ?? session.title
        await renamePersistedSession(session.key, title)
        return
      }
      if (error === 'archive') {
        await archiveSession(session.key)
        return
      }
      if (error === 'delete') {
        await deleteSession(session.key)
        return
      }
      if (error === 'unarchive') {
        await unarchiveSession(session.key)
        return
      }
      if (error === 'pin') {
        await pinPersistedSession(session.key, true)
        return
      }
      if (error === 'unpin') {
        await pinPersistedSession(session.key, false)
      }
    },
    [
      archiveSession,
      deleteSession,
      hydrateSession,
      pinPersistedSession,
      renamePersistedSession,
      unarchiveSession,
    ]
  )

  const archivedSessions = applySessionDisplayState(
    persistedArchived.map(
      (session) => pendingArchiveChanges[session.key] ?? session
    ),
    syncByKey,
    titleOverrides,
    pinOverrides
  ).filter((session) => session.archived)

  const retryActiveLoad = useCallback(async () => {
    await refreshActive()
    await refreshPinned()
  }, [refreshActive, refreshPinned])

  return {
    activeLoadFailed: Boolean(activeError),
    archiveSession,
    archiveSessions,
    archivedHasMore:
      persistedArchived.length === archivedLimit && archivedLimit > 0,
    archivedSessions,
    deleteSession,
    deleteSessions,
    deletedSessionKeys,
    hasArchivedSessions: [
      ...sessionsFromRolodexEntries(archivedExistenceEntries, siteId ?? ''),
      ...persistedArchived,
      ...Object.values(pendingArchiveChanges).filter(
        (session) => session.siteId === siteId
      ),
    ].some(
      (session) =>
        !deletedSessionKeys.has(session.key) &&
        pendingArchiveChanges[session.key]?.archived !== false
    ),
    hasExpandedArchived,
    hydrateSession,
    isLoadingActive: Boolean(activeQuery) && isLoadingActive,
    isLoadingArchived: hasExpandedArchived && isLoadingArchived,
    loadMoreArchived: () =>
      setArchivedLimit((current) => current + DOCUMENT_CHAT_SESSION_PAGE_SIZE),
    persistedSessions,
    pinPersistedSession,
    renamePersistedSession,
    retryActiveLoad,
    retrySession,
    toggleArchived: () => setHasExpandedArchived((expanded) => !expanded),
    unarchiveSession,
    unarchiveSessions,
  }
}
